import React, { useRef, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Camera, User as UserIcon, Loader, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Profile picture uploader (avatar circle + change button).
 * Two modes:
 *   - "self": uploads via POST /api/users/me/profile-picture (any signed-in role).
 *   - "admin": uploads via POST /api/admin/users/{targetUserId}/profile-picture (admin only).
 *
 * The "draftOnly" mode does NOT call the network — it returns a base64 data URL via onChange
 * so the caller can submit it later as part of a larger form (e.g. doctor/pharmacist creation).
 */
export const ProfilePictureUploader = ({
  value,
  onChange,
  mode = 'self',
  targetUserId = null,
  draftOnly = false,
  size = 96,
  className = '',
  testId = 'profile-pic-uploader'
}) => {
  const { t } = useLanguage();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('يرجى اختيار صورة', 'Please choose an image', 'Choisissez une image'));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('الصورة كبيرة جداً (الحد الأقصى 2 ميجا)', 'Image too large (max 2MB)', 'Image trop volumineuse (max 2 Mo)'));
      return;
    }

    setUploading(true);
    try {
      if (draftOnly) {
        const dataUrl = await fileToDataUrl(file);
        onChange && onChange(dataUrl);
      } else {
        const fd = new FormData();
        fd.append('file', file);
        const url = mode === 'admin' && targetUserId
          ? `${API}/admin/users/${targetUserId}/profile-picture`
          : `${API}/users/me/profile-picture`;
        const r = await axios.post(url, fd, { withCredentials: true });
        onChange && onChange(r.data.profile_picture);
        toast.success(t('تم تحديث الصورة', 'Profile picture updated', 'Photo mise à jour'));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل رفع الصورة', 'Upload failed', 'Échec du téléchargement'));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleClear = () => {
    onChange && onChange(null);
  };

  return (
    <div className={`flex items-center gap-4 ${className}`} data-testid={testId}>
      <div
        className="relative rounded-full bg-blue-50 border-4 border-white shadow-md overflow-hidden flex items-center justify-center flex-shrink-0"
        style={{ width: size, height: size }}
      >
        {value ? (
          <img src={value} alt="profile" className="w-full h-full object-cover" data-testid={`${testId}-preview`} />
        ) : (
          <UserIcon size={size * 0.55} className="text-[#1565c0]" strokeWidth={1.4} />
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <Loader className="text-white animate-spin" size={20} />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="rounded-xl"
          data-testid={`${testId}-pick-btn`}
        >
          <Camera size={14} className="me-2" />
          {value
            ? t('تغيير الصورة', 'Change Photo', 'Changer la photo')
            : t('رفع صورة', 'Upload Photo', 'Télécharger une photo')}
        </Button>
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClear}
            disabled={uploading}
            className="text-red-500 hover:text-red-600 rounded-xl"
            data-testid={`${testId}-clear-btn`}
          >
            <Trash2 size={14} className="me-1" />
            {t('إزالة', 'Remove', 'Supprimer')}
          </Button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
          className="hidden"
          onChange={(e) => handleFile(e.target.files && e.target.files[0])}
          data-testid={`${testId}-input`}
        />
      </div>
    </div>
  );
};

export default ProfilePictureUploader;
